import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Field,
  FieldGroup,
  FieldLabel,
  FieldSeparator,
  Flex,
  FormLabel,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/src/ui-components';
import { Input } from '@/src/ui-components/ui/input';
import { PlusCircleIcon, TrashIcon } from 'lucide-react';
import { Controller, useFieldArray, UseFieldArrayRemove, useFormContext } from 'react-hook-form';
import { PricePairOracle, PricePairOraclesForm } from '../../oracles.types';

type OraclePriceDetailsProps = {
  index: number;
  oraclePriceDetails: PricePairOracle;
  removeCard: UseFieldArrayRemove;
};

export const PricePairOracleCard = ({ index, removeCard }: OraclePriceDetailsProps) => {
  const { control } = useFormContext<PricePairOraclesForm>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: `pricePairOracles.${index}.oracles`,
  });

  const handleAddOracle = () => {
    append({ oracle_document_id: '0', account: '' });
  };

  return (
    <Card className="flex flex-col h-full shadow-md">
      <CardHeader>
        <CardTitle>Oracle Details</CardTitle>
        <CardDescription>Enter/Edit your oracle details below</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 flex-1">
        <FieldGroup className="flex flex-col md:flex-row align-center gap-4">
          <Controller
            name={`pricePairOracles.${index}.base_asset`}
            control={control}
            render={({ field }) => (
              <Field className="max-w-1/3">
                <FieldLabel htmlFor={`pricePairOracles.${index}.base_asset`}>Base Asset</FieldLabel>
                <Input id={`pricePairOracles.${index}.base_asset`} {...field} />
              </Field>
            )}
          />
          <Controller
            name={`pricePairOracles.${index}.quote_asset`}
            control={control}
            render={({ field }) => (
              <Field className="max-w-1/3">
                <FieldLabel htmlFor={`pricePairOracles.${index}.quote_asset`}>
                  Quote Asset
                </FieldLabel>
                <Input id={`pricePairOracles.${index}.quote_asset`} {...field} />
              </Field>
            )}
          />
          <Controller
            name={`pricePairOracles.${index}.network`}
            control={control}
            render={({ field }) => (
              <Field className="max-w-1/3">
                <FieldLabel htmlFor={`pricePairOracles.${index}.network`}>Network</FieldLabel>
                <Select onValueChange={field.onChange} {...field}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a network" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="xrpl:0">Mainnet</SelectItem>
                    <SelectItem value="xrpl:1">Testnet</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            )}
          />
        </FieldGroup>
        <FieldSeparator />
        <FieldGroup>
          <div className="flex items-center justify-between">
            <FormLabel>Sources</FormLabel>
            <Button type="button" onClick={handleAddOracle} variant="outline" size="sm">
              <PlusCircleIcon /> Add Source
            </Button>
          </div>
        </FieldGroup>
        <FieldGroup className="gap-6">
          {fields.map((field, idx) => (
            <Flex key={field.id} gap="4" align="end">
              <Controller
                control={control}
                name={`pricePairOracles.${index}.oracles.${idx}.oracle_document_id`}
                render={({ field }) => (
                  <Field className="max-w-1/2">
                    <FieldLabel
                      htmlFor={`pricePairOracles.${index}.oracles.${idx}.oracle_document_id`}
                    >
                      Oracle Document ID
                    </FieldLabel>
                    <Input
                      id={`pricePairOracles.${index}.oracles.${idx}.oracle_document_id`}
                      {...field}
                    />
                  </Field>
                )}
              />
              <Controller
                control={control}
                name={`pricePairOracles.${index}.oracles.${idx}.account`}
                render={({ field }) => (
                  <Field className="max-w-1/2">
                    <FieldLabel htmlFor={`pricePairOracles.${index}.oracles.${idx}.account`}>
                      Account
                    </FieldLabel>
                    <Input id={`pricePairOracles.${index}.oracles.${idx}.account`} {...field} />
                  </Field>
                )}
              />
              <Button
                type="button"
                className="self-end"
                variant="destructive"
                size="sm"
                onClick={() => remove(idx)}
              >
                <TrashIcon />
              </Button>
            </Flex>
          ))}
        </FieldGroup>
      </CardContent>
      <CardFooter>
        <Button
          type="button"
          variant="destructive"
          className="mx-auto"
          onClick={() => removeCard(index)}
        >
          <TrashIcon /> Remove Oracle Details
        </Button>
      </CardFooter>
    </Card>
  );
};
